import React, { useEffect, useState, useCallback } from "react";
import {
  setAdminPassword,
  clearAdminPassword,
  getAdminPassword,
  verifyAdmin,
  fetchAdminSubmissions,
  updateSubmissionHandled,
} from "@/lib/api";
import { useSEO, siteUrl } from "@/lib/seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  LogIn,
  LogOut,
  Lock,
  Loader2,
  Mail,
  Phone,
  Building2,
  Truck,
  CheckCircle2,
  RefreshCw,
  Inbox,
  Calendar,
  ExternalLink,
} from "lucide-react";

const TYPE_LABEL = {
  contact: "Contact",
  quote: "Offerte",
  demo: "Demo",
};
const TYPE_COLOR = {
  contact: "bg-white/10 text-white border-white/20",
  quote: "bg-[#00D4FF]/10 text-[#00D4FF] border-[#00D4FF]/30",
  demo: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30",
};

const LoginCard = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Admin — Wasz.nl",
    description: "Wasz.nl admin overzicht.",
    canonical: siteUrl("/admin"),
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await verifyAdmin(pw);
      setAdminPassword(pw);
      toast.success("Ingelogd");
      onLogin();
    } catch (err) {
      toast.error("Onjuist wachtwoord");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={onSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0F0F23] p-8 space-y-5"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/20 flex items-center justify-center">
            <Lock className="w-4 h-4 text-[#00D4FF]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#00D4FF] font-semibold">Admin</div>
            <h1 className="text-xl font-bold text-white tracking-tight">Wasz.nl inzendingen</h1>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-white/70 text-xs uppercase tracking-wider">Wachtwoord</Label>
          <Input
            data-testid="admin-password-input"
            type="password"
            autoFocus
            required
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="bg-[#050509] border-white/10 text-white h-11 focus-visible:ring-[#00D4FF]"
          />
        </div>
        <Button
          type="submit"
          data-testid="admin-login-submit"
          disabled={busy}
          className="w-full bg-[#00D4FF] hover:bg-white text-[#050509] font-bold h-11"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4" /> Inloggen</>}
        </Button>
        <p className="text-[11px] text-white/30 text-center">Beveiligd via X-Admin-Password header.</p>
      </form>
    </div>
  );
};

const SubmissionsList = ({ onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [showHandled, setShowHandled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (tab !== "all") params.type_filter = tab;
      if (!showHandled) params.handled = false;
      const data = await fetchAdminSubmissions(params);
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sessie verlopen — log opnieuw in.");
        onLogout();
      } else {
        toast.error("Kon inzendingen niet laden.");
      }
    } finally {
      setLoading(false);
    }
  }, [tab, showHandled, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const onToggleHandled = async (item) => {
    try {
      await updateSubmissionHandled(item.id, !item.handled);
      toast.success(item.handled ? "Hersteld" : "Gemarkeerd als afgehandeld");
      load();
    } catch (err) {
      toast.error("Bijwerken mislukt");
    }
  };

  const counts = items.reduce(
    (acc, it) => {
      acc.total++;
      acc[it.type] = (acc[it.type] || 0) + 1;
      return acc;
    },
    { total: 0 }
  );

  return (
    <div className="min-h-screen pt-10 pb-20">
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <header className="flex items-center justify-between mb-8">
          <div>
            <p className="section-overline mb-2">Admin</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Inzendingen</h1>
            <p className="text-white/50 text-sm mt-2">
              Aanvragen worden ook automatisch gemaild naar <span className="text-[#00D4FF]">info@wasz.nl</span> (mits Resend-key actief).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-refresh"
              onClick={load}
              variant="outline"
              size="sm"
              className="border-white/15 text-white hover:bg-white/5"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Ververs
            </Button>
            <Button
              data-testid="admin-logout"
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="border-white/15 text-white hover:bg-white/5"
            >
              <LogOut className="w-3.5 h-3.5" /> Uitloggen
            </Button>
          </div>
        </header>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <Tabs value={tab} onValueChange={setTab} className="w-fit">
            <TabsList className="bg-[#0F0F23] border border-white/10">
              <TabsTrigger data-testid="admin-tab-all" value="all">Alle</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-quote" value="quote">Offertes</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-demo" value="demo">Demo's</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-contact" value="contact">Contact</TabsTrigger>
            </TabsList>
          </Tabs>
          <label className="inline-flex items-center gap-2 text-sm text-white/60 cursor-pointer">
            <input
              type="checkbox"
              data-testid="admin-show-handled"
              checked={showHandled}
              onChange={(e) => setShowHandled(e.target.checked)}
              className="accent-[#00D4FF] w-4 h-4"
            />
            Toon afgehandelde
          </label>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/50">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laden…
          </div>
        ) : items.length === 0 ? (
          <div data-testid="admin-empty" className="rounded-2xl border border-white/10 bg-[#0F0F23] p-16 text-center">
            <Inbox className="w-10 h-10 text-white/20 mx-auto mb-4" />
            <p className="text-white/60">Geen inzendingen in deze weergave.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-submissions-list">
            <div className="text-xs text-white/40 mono">
              {items.length} {items.length === 1 ? "inzending" : "inzendingen"}
            </div>
            {items.map((it) => (
              <article
                key={it.id}
                data-testid={`admin-submission-${it.id}`}
                className={`rounded-xl border p-5 sm:p-6 transition-colors ${
                  it.handled ? "border-white/5 bg-[#0a0a18] opacity-70" : "border-white/10 bg-[#0F0F23]"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Badge className={`uppercase text-[10px] tracking-wider font-bold ${TYPE_COLOR[it.type]}`}>
                      {TYPE_LABEL[it.type] || it.type}
                    </Badge>
                    <span className="text-xs text-white/40 inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(it.created_at).toLocaleString("nl-NL", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    {it.handled && (
                      <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 text-[10px] uppercase">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Afgehandeld
                      </Badge>
                    )}
                  </div>
                  <Button
                    data-testid={`admin-toggle-${it.id}`}
                    onClick={() => onToggleHandled(it)}
                    variant="outline"
                    size="sm"
                    className={`border-white/15 text-xs ${
                      it.handled
                        ? "text-white/60 hover:bg-white/5"
                        : "text-[#10B981] hover:bg-[#10B981]/10 border-[#10B981]/30"
                    }`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {it.handled ? "Open zetten" : "Markeer als afgehandeld"}
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm mb-4">
                  <div className="text-white">
                    <span className="text-white/40 text-xs uppercase tracking-wider mr-2">Naam</span>
                    {it.name}
                  </div>
                  <div className="text-white">
                    <Mail className="w-3.5 h-3.5 inline text-[#00D4FF] mr-1.5" />
                    <a href={`mailto:${it.email}`} className="hover:text-[#00D4FF]">{it.email}</a>
                    <a href={`mailto:${it.email}`} className="ml-2 text-white/30 hover:text-[#00D4FF]">
                      <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  </div>
                  {it.phone && (
                    <div className="text-white/80">
                      <Phone className="w-3.5 h-3.5 inline text-[#00D4FF] mr-1.5" />
                      <a href={`tel:${it.phone}`} className="hover:text-[#00D4FF]">{it.phone}</a>
                    </div>
                  )}
                  {it.company && (
                    <div className="text-white/80">
                      <Building2 className="w-3.5 h-3.5 inline text-[#00D4FF] mr-1.5" />
                      {it.company}
                    </div>
                  )}
                  {it.fleet_size && (
                    <div className="text-white/80">
                      <Truck className="w-3.5 h-3.5 inline text-[#00D4FF] mr-1.5" />
                      Wagenpark: {it.fleet_size}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-white/5 bg-[#050509]/60 p-4 text-sm text-white/75 whitespace-pre-line leading-relaxed">
                  {it.message}
                </div>

                <div className="mt-3 text-[10px] mono text-white/30">
                  ID: {it.id} · email: {it.email_status}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useSEO({
    title: "Admin — Wasz.nl",
    description: "Wasz.nl admin overzicht.",
    canonical: siteUrl("/admin"),
  });

  useEffect(() => {
    const pw = getAdminPassword();
    if (!pw) {
      setChecking(false);
      return;
    }
    verifyAdmin(pw)
      .then(() => setAuthed(true))
      .catch(() => clearAdminPassword())
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  return authed ? (
    <SubmissionsList
      onLogout={() => {
        clearAdminPassword();
        setAuthed(false);
      }}
    />
  ) : (
    <LoginCard onLogin={() => setAuthed(true)} />
  );
};

export default Admin;
